import { useContext, useMemo, useCallback } from 'react';

import { IItemKey } from 'api/catalog/models';
import { TableViewContext } from 'providers';
import { UNSORTABLE_COLUMNS } from 'constants/table';

import { ItemAttributeInfo, useItemAttributes } from './useItemAttributes';

interface ColumnInfo {
  minWidth?: number;
  align?: 'left' | 'center' | 'right';
  isSortable?: boolean;
  customSortLabel?: string;
  initialSort?: 'asc' | 'desc';
}

export type ProductListingColumn = ColumnInfo & ItemAttributeInfo;

const DEFAULTS: ColumnInfo = {
  align: 'left',
  isSortable: true,
  initialSort: 'asc',
};

export const useProductListing = () => {
  const { visibleFields = [] } = useContext(TableViewContext);
  const { attributeMap } = useItemAttributes();

  const getDefaults = useCallback(
    (key: IItemKey): ColumnInfo => ({
      ...DEFAULTS,
      isSortable: !UNSORTABLE_COLUMNS.includes(key),
    }),
    []
  );

  const getAdditionalColumnInfo = useCallback(
    (key: IItemKey): ColumnInfo => {
      switch (key) {
        case 'brandId':
        case 'categoryId':
        case 'departmentId':
        case 'subcategoryId':
        case 'minOrderQty':
        case 'maxOrderQty':
        case 'minShopOrderValue':
        case 'grossWeight':
        case 'countryOfOriginCode':
        case 'packQty':
          return {
            ...getDefaults(key),
            align: 'right',
          };
        default: {
          return getDefaults(key);
        }
      }
    },
    [getDefaults]
  );

  const columns: ProductListingColumn[] = useMemo(() => {
    return visibleFields.map((colKey) => ({
      ...attributeMap[colKey],
      ...getAdditionalColumnInfo(colKey),
    }));
  }, [attributeMap, getAdditionalColumnInfo, visibleFields]);

  return { columns };
};
